import { IconButton, InputAdornment, SelectChangeEvent, TextField } from "@mui/material";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import errorIcon from "../../assets/icons/error-icon.svg";
import instantmixIcon from "../../assets/icons/instantmix-icon.svg";
import searchIcon from "../../assets/icons/search-icon.svg";
import CloseIconRed from "../../assets/icons/close-icon-red.svg";
import MultiSelectDropdown from "../../shared/components/multi-select-dropdown/MultiSelectDropdown";

interface CampaignsFiltersProps {
  handleStatusChange: (e: SelectChangeEvent<string[]>) => void;
  handleDatasetChange: (e: SelectChangeEvent<string[]>) => void;
  handleCreatedByChange: (e: SelectChangeEvent<string[]>) => void;
  handleLastEditedChange: (e: SelectChangeEvent<string[]>) => void;
  handleBrandChange: (e: SelectChangeEvent<string[]>) => void;
  status: string[];
  cadence: string[];
  createdBy: string[];
  lastEditedBy: string[];
  destination: string[];
  statusOptions: string[];
  cadenceOptions: string[];
  createdByOptions: string[];
  lastEditedByOptions: string[];
  destinationOptions: string[];
  clearFilters: () => void;
}

const CampaignsFilters = ({
  handleStatusChange,
  handleDatasetChange,
  handleCreatedByChange,
  handleLastEditedChange,
  handleBrandChange,
  status,
  cadence,
  createdBy,
  lastEditedBy,
  destination,
  statusOptions,
  cadenceOptions,
  createdByOptions,
  lastEditedByOptions,
  destinationOptions,
  clearFilters,
}: CampaignsFiltersProps) => {
  const methods = useFormContext();
  const { control, setValue, setError, clearErrors } = methods;

  const [areFiltersVisible, setAreFiltersVisible] = useState(false);

  const toggleFiltersVisibility = () => {
    setAreFiltersVisible((prev) => !prev);
  };

  return (
    <div className="campaigns__container__filters-wrap">
      <div className="campaigns__container__filters-wrap__left">
        <div className="campaigns__container__filters-wrap__input">
          <Controller
            control={control}
            name="search"
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  className="form-control-container__text-field"
                  required
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  placeholder="Search campaign"
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 100 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <img src={searchIcon} alt="loop" />
                        </InputAdornment>
                      ),
                      endAdornment: field.value && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setValue("search", "");
                              clearErrors("search");
                            }}
                          >
                            x
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value.length === 100) {
                      setError("search", {
                        type: "maxLength",
                        message: "Search query cannot exceed 100 characters",
                      });
                    } else {
                      clearErrors("search");
                    }

                    field.onChange(value);
                  }}
                />

                {error?.message && (
                  <div className="create-campaign__input-wrap__segment-err">
                    <img src={errorIcon} alt="Error Icon" />
                    {error.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {areFiltersVisible && (
          <div className="campaigns__container__filters-row2">
            <div className="campaigns__container__filters-wrap__form-control-container-wrap">
              <label className="filter-label" htmlFor="status-select">
                Status
                <MultiSelectDropdown
                  id="status-select"
                  label="Status"
                  options={statusOptions}
                  selectedValues={status}
                  onChange={handleStatusChange}
                />
              </label>
            </div>

            <div className="campaigns__container__filters-wrap__form-control-container-wrap">
              <label className="filter-label" htmlFor="cadence-select">
                Cadence
                <MultiSelectDropdown
                  id="cadence-select"
                  label="Cadence"
                  options={cadenceOptions}
                  selectedValues={cadence}
                  onChange={handleDatasetChange}
                />
              </label>
            </div>
            <div className="campaigns__container__filters-wrap__form-control-container-wrap">
              <label className="filter-label" htmlFor="createdBy-select">
                Created by
                <MultiSelectDropdown
                  id="createdBy-select"
                  label="Created By"
                  options={createdByOptions}
                  selectedValues={createdBy}
                  onChange={handleCreatedByChange}
                />
              </label>
            </div>
            <div className="campaigns__container__filters-wrap__form-control-container-wrap">
              <label className="filter-label" htmlFor="lastEdited-select">
                Last edited by
                <MultiSelectDropdown
                  id="lastEdited-select"
                  label="Last Edited by"
                  options={lastEditedByOptions}
                  selectedValues={lastEditedBy}
                  onChange={handleLastEditedChange}
                />
              </label>
            </div>
            <div className="campaigns__container__filters-wrap__form-control-container-wrap">
              <label className="filter-label" htmlFor="destination-select">
                Destination
                <MultiSelectDropdown
                  id="destination-select"
                  label="Destination"
                  options={destinationOptions}
                  selectedValues={destination}
                  onChange={handleBrandChange}
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="campaigns__container__filters-wrap__right">
        <div className="campaigns__container__filters-wrap__show-filters">
          <button
            type="button"
            className="btn-secondary btn-border"
            onClick={toggleFiltersVisibility}
          >
            {!areFiltersVisible ? "Show Filters" : "Hide Filters"}
            <img
              className="btn-secondary__icon"
              src={instantmixIcon}
              alt={areFiltersVisible ? "Hide filters" : "Show filters"}
            />
          </button>
        </div>

        {areFiltersVisible && (
          <div className="campaigns__container__filters-wrap__clear-filters">
            <button
              type="button"
              id="clear-filters-button"
              name="clear-filters-button"
              className="btn-secondary no-border"
              onClick={clearFilters}
            >
              Clear
              <img className="btn-secondary__icon" src={CloseIconRed} alt="Clear filters" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsFilters;
